import { usePostFrontVerificaTipoVerifica } from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import { StyledHtmlText } from '@framework/engine';
import { useTranslation } from '@framework/i18n';
import {
  AlignRow,
  LoaderSpin,
  LoginCard,
  useBreadcrumbItem,
  useHistory,
} from '@framework/ui';
import { Button, Card, Result, Space, message } from 'antd';
import { ResultStatusType } from 'antd/es/result';
import DOMPurify from 'dompurify';
import qs from 'qs';
import * as React from 'react';
import { useState } from 'react';
import { down } from 'styled-breakpoints';
import styled from 'styled-components';
import { PageLayout } from '../../ui/layout/PageLayout';
import { getVerificationLabels } from './getVerificationLabels';
import { getVerificationResultInfo } from './getVerificationResultInfo';

const StyledVerificationCard = styled(Card)`
  width: 550px;
  ${down('md')} {
    min-width: 450px;
  }
  ${down('sm')} {
    min-width: unset;
    width: calc(100vw - 40px);
    > .ant-card-body {
      padding: 24px 12px;
    }
  }
`;

export interface IVerificationPageProps {}

/**
 * Componente di verifica.
 */
export function VerificationPage(props: IVerificationPageProps) {
  const { t } = useTranslation();
  useBreadcrumbItem({
    path: '/verifica',
    title: t('verification-page.verifica', 'Verifica'),
  });
  const history = useHistory();
  const verificationMutation = usePostFrontVerificaTipoVerifica();
  const [showResult, setShowResult] = useState(false);

  const queryParams = qs.parse(history.location.search, {
    ignoreQueryPrefix: true,
  });

  const { tipoVerifica, tipoverifica, ...allRequestParams } =
    queryParams as Record<string, string>;

  const handleVerification = async () => {
    try {
      await verificationMutation.mutateAsync({
        tipoVerifica: tipoVerifica ?? tipoverifica,
        // Non è corretta la specifica generata da Orval rispetto all'OpenAPI
        params: { ...allRequestParams } as any,
      });
      setShowResult(true);
    } catch (error) {
      console.error(
        t('verification-page.errore-in-verifica', 'Errore durante la verifica'),
        error,
      );
      message.error(getNetworkErrorMessage(error, t("verification-page.errore-durante-la-verifica", 'Si è verificato un problema durante la verifica'))); // prettier-ignore
    }
  };

  const { status, message: verificationMessage } = getVerificationResultInfo(
    verificationMutation.data,
  );

  const { title, subTitle, buttonText } = getVerificationLabels(tipoVerifica);

  return (
    <PageLayout>
      <AlignRow justify="center">
        <LoaderSpin spinning={verificationMutation.isLoading} transparent>
          {!showResult ? (
            <LoginCard title={title} subTitle={subTitle}>
              <Space
                direction="vertical"
                size="large"
                style={{ width: '100%' }}
              >
                <Button
                  size="large"
                  type="primary"
                  block
                  // eslint-disable-next-line @typescript-eslint/no-misused-promises
                  onClick={handleVerification}
                >
                  {buttonText}
                </Button>
              </Space>
            </LoginCard>
          ) : (
            <StyledVerificationCard bordered={false}>
              <Result
                status={status as ResultStatusType}
                title={
                  <StyledHtmlText
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(verificationMessage),
                    }}
                  />
                }
              />
            </StyledVerificationCard>
          )}
        </LoaderSpin>
      </AlignRow>
    </PageLayout>
  );
}
